import React from 'react';
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Stack,
  Card
} from '@mui/material';
import { useLocales } from '../../locales';
import { BreakTimesEditor } from './BreakTimesEditor';

const MIN_CAPACITY = 1;
const MAX_CAPACITY = 30000;

export const DayScheduleForm = ({ schedule, onChange, title }) => {
  const { translate } = useLocales();

  const validateCapacity = (value) => {
    if (value < MIN_CAPACITY) return translate('event_settings.capacity_min_error') || `Minimum capacity is ${MIN_CAPACITY}`;
    if (value > MAX_CAPACITY) return translate('event_settings.capacity_max_error') || `Maximum capacity is ${MAX_CAPACITY}`;
    return null;
  };

  const formatTimeDisplay = (time) => {
    if (!time) return '';
    const hour = time.substring(0, 2);
    const min = time.substring(2, 4);
    return `${hour}:${min}`;
  };

  const handleTimeChange = (field, value) => {
    // Remove any colons and ensure 4 digits
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue.length <= 4) {
      onChange({ ...schedule, [field]: cleanValue });
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    if (!schedule.enabled || !schedule.startTime || !schedule.endTime) {
      return { totalSlots: 0, totalHours: 0, totalCapacity: 0 };
    }

    const startHour = parseInt(schedule.startTime.substring(0, 2));
    const startMin = parseInt(schedule.startTime.substring(2, 4));
    const endHour = parseInt(schedule.endTime.substring(0, 2));
    const endMin = parseInt(schedule.endTime.substring(2, 4));

    const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    const totalHours = totalMinutes / 60;

    // Calculate break time
    let breakMinutes = 0;
    schedule.breakTimes?.forEach(breakTime => {
      const breakStart = parseInt(breakTime.startTime.substring(0, 2)) * 60 +
                         parseInt(breakTime.startTime.substring(2, 4));
      const breakEnd = parseInt(breakTime.endTime.substring(0, 2)) * 60 +
                       parseInt(breakTime.endTime.substring(2, 4));
      breakMinutes += (breakEnd - breakStart);
    });

    const activeMinutes = totalMinutes - breakMinutes;
    const totalSlots = Math.floor(activeMinutes / schedule.slotDuration);
    const totalCapacity = totalSlots * schedule.slotCapacity;

    return { totalSlots, totalHours: totalHours.toFixed(1), totalCapacity };
  };

  const stats = calculateStats();

  return (
    <Stack spacing={3}>
      <Typography variant="h6">{title}</Typography>

      <FormControlLabel
        control={
          <Switch
            checked={schedule.enabled}
            onChange={(e) => onChange({ ...schedule, enabled: e.target.checked })}
          />
        }
        label={translate('event_settings.enable_this_day')}
      />

      {schedule.enabled && (
        <>
          {/* Operating Hours */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={translate('event_settings.opening_time')}
                value={formatTimeDisplay(schedule.startTime)}
                onChange={(e) => handleTimeChange('startTime', e.target.value)}
                placeholder="09:00"
                helperText={translate('event_settings.time_format_help')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={translate('event_settings.closing_time')}
                value={formatTimeDisplay(schedule.endTime)}
                onChange={(e) => handleTimeChange('endTime', e.target.value)}
                placeholder="17:00"
                helperText={translate('event_settings.time_format_help')}
              />
            </Grid>
          </Grid>

          {/* Slot Configuration */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={translate('event_settings.slot_duration_minutes')}
                value={schedule.slotDuration}
                onChange={(e) => {
                  let val = parseInt(e.target.value) || 30;
                  const startTotal = parseInt(schedule.startTime?.substring(0, 2) || '0') * 60 + parseInt(schedule.startTime?.substring(2, 4) || '0');
                  const endTotal = parseInt(schedule.endTime?.substring(0, 2) || '0') * 60 + parseInt(schedule.endTime?.substring(2, 4) || '0');
                  const maxDuration = endTotal - startTotal;
                  if (maxDuration > 0 && val > maxDuration) val = maxDuration;
                  onChange({ ...schedule, slotDuration: val });
                }}
                InputProps={{ inputProps: { min: 15, max: 480, step: 15 } }}
                helperText={translate('event_settings.duration_help')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={translate('event_settings.capacity_per_slot')}
                value={schedule.slotCapacity}
                onChange={(e) => onChange({
                  ...schedule,
                  slotCapacity: parseInt(e.target.value) || 1
                })}
                error={!!validateCapacity(schedule.slotCapacity)}
                InputProps={{ inputProps: { min: 1, max: 30000 } }}
                helperText={validateCapacity(schedule.slotCapacity) ?? translate('event_settings.capacity_help')}
              />
            </Grid>
          </Grid>

          {/* Break Times */}
          <BreakTimesEditor
            breakTimes={schedule.breakTimes || []}
            onChange={(breakTimes) => onChange({ ...schedule, breakTimes })}
          />

          {/* Preview Statistics */}
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'primary.lighter', borderColor: 'primary.light' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary' }}>
              {translate('event_settings.preview')}
            </Typography>
            <Grid container spacing={2} direction="row-reverse">
              <Grid item xs={12} sm={4}>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary">
                    {translate('event_settings.operating_hours')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="text.primary">
                    {formatTimeDisplay(schedule.startTime)} - {formatTimeDisplay(schedule.endTime)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary">
                    {translate('event_settings.total_slots')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="text.primary">
                    {stats.totalSlots} {translate('event_settings.slots')}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary">
                    {translate('event_settings.daily_capacity')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="text.primary">
                    {stats.totalCapacity} {translate('event_settings.bookings')}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </>
      )}
    </Stack>
  );
};